// Games.jsx con paleta optimizada
import React, { useState } from 'react';
import { 
  FaGamepad, 
  FaBrain, 
  FaHeart, 
  FaPuzzlePiece, 
  FaRunning, 
  FaMusic,
  FaPlay, 
  FaTimes, 
  FaExpand,
  FaStar,
  FaClock,
  FaUsers,
  FaArrowLeft,
  FaPaw
} from 'react-icons/fa';

// Importar configuraciones de juegos
import { 
  gamesDatabase, 
  gameCategories, 
  getGamesByCategory,
  getFeaturedGames 
} from '../../data/gamesConfig';

const Games = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('todas');

  // Usar datos de configuración
  const getFilteredGames = () => {
    return getGamesByCategory(selectedCategory);
  };

  const openGame = (game) => {
    // Si el juego no se puede incrustar, abrirlo en nueva pestaña
    if (game.embedable === false) {
      window.open(game.url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Si se puede incrustar, abrir en modal
    setSelectedGame(game);
  };

  const closeGame = () => {
    setSelectedGame(null);
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Fácil': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'Medio': return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'Difícil': return 'bg-rose-100 text-rose-700 border border-rose-200';
      default: return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  // Mapear iconos de strings a componentes
  const getIconComponent = (iconName) => {
    const iconMap = {
      'FaGamepad': FaGamepad,
      'FaBrain': FaBrain,
      'FaHeart': FaHeart,
      'FaPuzzlePiece': FaPuzzlePiece,
      'FaRunning': FaRunning,
      'FaMusic': FaMusic
    };
    return iconMap[iconName] || FaGamepad;
  };

  // Categorías optimizadas con nueva paleta
  const optimizedCategories = [
    { 
      id: 'todas', 
      name: 'Todos los Juegos', 
      icon: 'FaGamepad', 
      color: 'from-sky-400 to-emerald-400',
      description: 'Explora todos nuestros juegos educativos'
    },
    { 
      id: 'logica', 
      name: 'Lógica y Puzzles', 
      icon: 'FaBrain', 
      color: 'from-emerald-400 to-teal-400',
      description: 'Desarrolla tu pensamiento lógico'
    },
    { 
      id: 'empatia', 
      name: 'Empatía Animal', 
      icon: 'FaHeart', 
      color: 'from-pink-400 to-rose-400',
      description: 'Aprende sobre el cuidado de los animales'
    },
    { 
      id: 'creatividad', 
      name: 'Creatividad', 
      icon: 'FaPuzzlePiece', 
      color: 'from-teal-400 to-emerald-400',
      description: 'Expresa tu lado artístico'
    },
    { 
      id: 'actividad', 
      name: 'Actividad Física', 
      icon: 'FaRunning', 
      color: 'from-amber-400 to-orange-400',
      description: 'Juegos que te mantienen activo'
    },
    { 
      id: 'musica', 
      name: 'Música y Sonidos', 
      icon: 'FaMusic', 
      color: 'from-emerald-400 to-teal-500',
      description: 'Explora el mundo de la música'
    }
  ];

  const filteredGames = getFilteredGames();
  const currentCategory = optimizedCategories.find(cat => cat.id === selectedCategory);
  const featuredGames = getFeaturedGames();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 py-16 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 text-6xl text-emerald-500 animate-bounce">🾾</div>
        <div className="absolute top-40 right-20 text-4xl text-teal-500 animate-pulse">🦋</div>
        <div className="absolute bottom-32 left-20 text-5xl text-sky-500 animate-bounce delay-300">🌿</div>
        <div className="absolute bottom-20 right-32 text-3xl text-emerald-600 animate-pulse delay-700">🛵</div>
        <div className="absolute top-60 left-1/2 text-4xl text-teal-400 animate-bounce delay-500">🌱</div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-3 bg-white/80 rounded-full px-8 py-4 backdrop-blur-sm mb-6 shadow-lg border border-emerald-100">
            <FaPaw className="text-emerald-600 text-xl" />
            <span className="font-medium text-emerald-800">Laboratorio de Empatía Animal</span>
            <FaPaw className="text-emerald-600 text-xl" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-emerald-900 mb-4 leading-tight">
            Juegos
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {' '}de Empatía
            </span>
          </h1>
          
          <p className="text-lg text-emerald-700 max-w-3xl mx-auto leading-relaxed">
            Descubre juegos mágicos que te ayudarán a conectar con el mundo animal 
            y desarrollar tu capacidad de cuidar y comprender a todas las criaturas.
          </p>

          {/* Juegos destacados */}
          {featuredGames.length > 0 && (
            <div className="mt-8 p-6 bg-gradient-to-r from-emerald-100/50 to-teal-100/50 rounded-2xl border border-emerald-200/50 backdrop-blur-sm">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <FaStar className="text-amber-500" />
                <span className="font-medium text-emerald-800">Juegos Destacados</span>
                <FaStar className="text-amber-500" />
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {featuredGames.slice(0, 3).map((game) => (
                  <button
                    key={game.id}
                    onClick={() => openGame(game)}
                    className="inline-flex items-center space-x-2 bg-white/70 hover:bg-white/90 px-4 py-2 rounded-full text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    <span>{game.thumbnail}</span>
                    <span>{game.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filtros de categorías */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {optimizedCategories.map((category) => {
            const IconComponent = getIconComponent(category.icon);
            const isActive = selectedCategory === category.id;
            const gameCount = getGamesByCategory(category.id).length;
            
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group flex items-center space-x-3 px-6 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
                  isActive
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg border-2 border-white/30`
                    : 'bg-white/80 text-emerald-700 hover:bg-white/90 border-2 border-emerald-100/50 backdrop-blur-sm'
                }`}
              >
                <IconComponent className={`text-lg ${isActive ? 'text-white' : 'text-emerald-600'}`} />
                <span>{category.name}</span>
                <span className={`text-sm px-2 py-1 rounded-full font-bold ${
                  isActive ? 'bg-white/30 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {gameCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Descripción de categoría actual */}
        {currentCategory && selectedCategory !== 'todas' && (
          <div className={`bg-gradient-to-r ${currentCategory.color} p-8 rounded-3xl text-white text-center mb-10 shadow-xl border border-white/20`}>
            {React.createElement(getIconComponent(currentCategory.icon), { 
              className: "text-4xl mx-auto mb-4 drop-shadow-md" 
            })}
            <h2 className="text-3xl font-bold mb-3 drop-shadow-md">{currentCategory.name}</h2>
            <p className="text-white/90 text-lg max-w-2xl mx-auto">{currentCategory.description}</p>
          </div>
        )}

        {/* Grid de juegos */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGames.map((game) => (
            <div
              key={game.id}
              className="group bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500 overflow-hidden cursor-pointer border border-emerald-100/50 hover:border-emerald-200"
              onClick={() => openGame(game)}
            >
              {/* Thumbnail con overlay */}
              <div className="bg-gradient-to-br from-emerald-100 to-teal-100 h-48 flex items-center justify-center text-7xl relative overflow-hidden group-hover:scale-110 transition-transform duration-500">
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative z-10 filter drop-shadow-lg">
                  {game.thumbnail}
                </span>
                {game.featured && (
                  <FaStar className="absolute top-3 right-3 text-amber-400 text-xl drop-shadow-lg" />
                )}
              </div>

              {/* Contenido */}
              <div className="p-6">
                {/* Header del juego */}
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-bold text-xl text-emerald-900 group-hover:text-teal-700 transition-colors line-clamp-2">
                    {game.title}
                  </h3>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${getDifficultyColor(game.difficulty)}`}>
                    {game.difficulty}
                  </span>
                </div>

                {/* Descripción */}
                <p className="text-emerald-700 text-sm mb-4 leading-relaxed line-clamp-3">
                  {game.description}
                </p>

                {/* Información del juego */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-xs text-emerald-600">
                    <FaClock className="text-teal-500" />
                    <span className="font-medium">{game.duration}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-emerald-600">
                    <FaUsers className="text-teal-500" />
                    <span className="font-medium">{game.players}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-emerald-600">
                    <FaStar className="text-amber-500" />
                    <span className="font-medium">{game.ageGroup}</span>
                  </div>
                </div>

                {/* Habilidades */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {game.skills.slice(0, 2).map((skill, index) => (
                    <span key={index} className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium border border-emerald-200">
                      {skill}
                    </span>
                  ))}
                  {game.skills.length > 2 && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-3 py-1 rounded-full font-medium border border-teal-200">
                      +{game.skills.length - 2}
                    </span>
                  )}
                </div>

                {/* Botón jugar */}
                <button className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-full font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 border-2 border-emerald-400 hover:border-emerald-300">
                  <FaPlay className="text-xs" />
                  <span>{game.embedable === false ? 'Abrir Juego' : 'Jugar Ahora'}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Mensaje si no hay juegos */}
        {filteredGames.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-emerald-100/50 max-w-md mx-auto">
              <FaPaw className="text-emerald-400 text-8xl mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-emerald-800 mb-4">
                Pronto más aventuras
              </h3>
              <p className="text-emerald-600 leading-relaxed">
                Estamos preparando más juegos mágicos para que explores 
                el maravilloso mundo de los animales.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de juego */}
      {selectedGame && (
        <div className="fixed inset-0 z-50 bg-emerald-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-6xl max-h-[95vh] w-full overflow-hidden shadow-2xl border border-emerald-200">
            {/* Header del modal */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-3xl filter drop-shadow-lg">{selectedGame.thumbnail}</span>
                <div>
                  <h3 className="text-2xl font-bold drop-shadow-md">{selectedGame.title}</h3>
                  <p className="text-white/90 text-sm mt-1">{selectedGame.description}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => window.open(selectedGame.url, '_blank')}
                  className="p-3 bg-white/20 rounded-full hover:bg-white/30 transition-colors backdrop-blur-sm border border-white/30"
                  title="Abrir en pantalla completa"
                >
                  <FaExpand className="text-lg" />
                </button>
                <button
                  onClick={closeGame}
                  className="p-3 bg-white/20 rounded-full hover:bg-white/30 transition-colors backdrop-blur-sm border border-white/30"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
            </div>

            {/* Iframe del juego */}
            <div className="relative" style={{ height: '70vh' }}>
              {selectedGame.embedable !== false ? (
                <iframe
                  src={selectedGame.url}
                  className="w-full h-full border-0"
                  title={selectedGame.title}
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
                  <div className="text-center bg-white/80 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-emerald-200">
                    <span className="text-8xl mb-6 block filter drop-shadow-lg">{selectedGame.thumbnail}</span>
                    <h3 className="text-2xl font-bold mb-4 text-emerald-800">Este juego se abre en una nueva pestaña</h3>
                    <p className="text-emerald-600 mb-6 max-w-md">Por razones de seguridad, algunos juegos no se pueden mostrar aquí</p>
                    <button
                      onClick={() => window.open(selectedGame.url, '_blank')}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8 py-4 rounded-full font-bold hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-2 border-emerald-400"
                    >
                      Abrir Juego
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer con información */}
            <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 flex flex-wrap items-center justify-between gap-4 border-t border-emerald-100">
              <div className="flex flex-wrap gap-6 text-sm text-emerald-700">
                <span className="flex items-center space-x-2 bg-white/70 px-3 py-2 rounded-full">
                  <FaClock className="text-teal-500" />
                  <span className="font-medium">{selectedGame.duration}</span>
                </span>
                <span className="flex items-center space-x-2 bg-white/70 px-3 py-2 rounded-full">
                  <FaUsers className="text-teal-500" />
                  <span className="font-medium">{selectedGame.players}</span>
                </span>
                <span className={`px-3 py-2 rounded-full text-xs font-medium ${getDifficultyColor(selectedGame.difficulty)}`}>
                  {selectedGame.difficulty}
                </span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {selectedGame.skills.map((skill, index) => (
                  <span key={index} className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium border border-emerald-200">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;